import { css } from "@emotion/css";
import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { EmptyStateNoResults, InlineEmptyStateOnboarding, List, ListItemContent, ListItemLink, Pagination, SearchInput } from "@octopusdeploy/design-system-components";
import { borderRadius, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { LibraryVariableSetResource, Repository, ResourceCollection } from "@octopusdeploy/octopus-server-client";
import { Permission, VariableSetContentType } from "@octopusdeploy/octopus-server-client";
import { links, type QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useCallback } from "react";
import { AddVariableSetDialogLayout } from "~/areas/library/components/VariableSets/AddVariableSetDialogLayout";
import Dialog from "~/components/Dialog/Dialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import MarkdownDescription from "~/components/MarkdownDescription";
import { formatUrl } from "~/components/Navigation/ExternalLink/ExternalLink";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import ListTitle from "~/primitiveComponents/dataDisplay/ListTitle/ListTitle";
interface VariableSetsPageProps {
    spaceId: string;
    queryParams: LibraryVariableSetsPageQueryParams;
    setQueryParams: QueryParamValuesSetter<LibraryVariableSetsPageQueryParams>;
    loaderData: LibraryVariableSetsPageLoaderData;
}
export interface LibraryVariableSetsPageQueryParams {
    variableSetName: string;
    page: number | undefined;
    pageSize: number | undefined;
}
interface LibraryVariableSetsPageLoaderData {
    variableSets: ResourceCollection<LibraryVariableSetResource>;
    hasVariableSets: boolean;
}
export async function libraryVariableSetsPageLoader(repository: Repository, queryParams: LibraryVariableSetsPageQueryParams): Promise<LibraryVariableSetsPageLoaderData> {
    return getPaginatedVariableSets(repository, queryParams);
}
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
const pageTitle = "Variable Sets";
export function VariableSetsPage({ spaceId, queryParams, setQueryParams, loaderData }: VariableSetsPageProps) {
    const addVariableSetDialogControls = useDialogTrigger();
    const [debouncedVariableSetName, setDebouncedVariableSetName] = useDebouncedExternalState(queryParams.variableSetName, (value) => setQueryParams({ variableSetName: value, page: 1 }), 200);
    const { result: variableSetsResult } = usePagedVariableSetsQuery(queryParams, loaderData);
    const { variableSets, hasVariableSets } = variableSetsResult;
    const addVariableSetPageAction: PrimaryPageAction = {
        type: "button",
        hasPermissions: isAllowed({ permission: Permission.LibraryVariableSetCreate }),
        label: "Add Variable Set",
        onClick: addVariableSetDialogControls.openDialog,
    };
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    const dialogs = (<Dialog open={addVariableSetDialogControls.isOpen}>
            <AddVariableSetDialogLayout spaceId={spaceId}/>
        </Dialog>);
    if (!hasVariableSets) {
        return (<PageContent header={{ title: pageTitle, primaryAction: addVariableSetPageAction }}>
                <ContentBorder>
                    {dialogs}
                    <InlineEmptyStateOnboarding entityName={"Variable Sets"} primaryAction={addVariableSetPageAction} learnMore={{ label: "Docs: Variable Sets", path: formatUrl("LibraryVariableSets") }}/>
                </ContentBorder>
            </PageContent>);
    }
    const hideBottomPagination = variableSets.Items.length < 20;
    return (<PageContent header={{ title: pageTitle, primaryAction: addVariableSetPageAction }} filters={{ inputs: [<SearchInput value={debouncedVariableSetName} onChange={setDebouncedVariableSetName} accessibleName={"Search by tag set name"} placeholder={"Filter by name..."}/>] }} pagination={{
            ui: (<Pagination label="Tag Sets" totalResults={variableSets.TotalResults} itemsPerPageOptions={pageSizeOptions} selectedItemsPerPage={variableSets.ItemsPerPage} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>),
            placement: hideBottomPagination ? "top" : "topAndBottom",
        }}>
            <ContentBorder>
                {dialogs}
                <List items={variableSets.Items} empty={<EmptyStateNoResults />} renderRow={({ item: variableSet }) => <LibraryVariableSetListItem variableSet={variableSet}/>} rowKey={(item) => item.Id}/>
            </ContentBorder>
        </PageContent>);
}
interface GetPaginatedVariableSetsOptions {
    page: number | undefined;
    pageSize: number | undefined;
    variableSetName: string | undefined;
}
async function getPaginatedVariableSets(repository: Repository, { page = 1, pageSize = defaultPageSize, variableSetName = "" }: GetPaginatedVariableSetsOptions) {
    const libraryVariableSets = repository.LibraryVariableSets.list({
        skip: (page - 1) * pageSize,
        take: pageSize,
        partialName: variableSetName,
        contentType: VariableSetContentType.Variables,
    });
    const libraryVariableSetsStats = repository.LibraryVariableSets.list({ skip: 0, take: 0 });
    return {
        variableSets: await libraryVariableSets,
        hasVariableSets: (await libraryVariableSetsStats).TotalResults > 0,
    };
}
function usePagedVariableSetsQuery(queryParams: LibraryVariableSetsPageQueryParams, initialResults: LibraryVariableSetsPageLoaderData) {
    return useQuery((repository) => getPaginatedVariableSets(repository, { page: queryParams.page, pageSize: queryParams.pageSize, variableSetName: queryParams.variableSetName }), [queryParams.page, queryParams.pageSize, queryParams.variableSetName], "Get paginated variable sets", { initialResult: initialResults });
}
interface LibraryVariableSetListItemProps {
    variableSet: LibraryVariableSetResource;
}
function LibraryVariableSetListItem({ variableSet }: LibraryVariableSetListItemProps) {
    return (<ListItemLink href={links.editVariableSetPage.generateUrl({ spaceId: variableSet.SpaceId, variableSetId: variableSet.Id })}>
            <ListItemContent>
                <ListTitle>{variableSet.Name}</ListTitle>
                <MarkdownDescription markup={variableSet.Description}/>
            </ListItemContent>
        </ListItemLink>);
}
function ContentBorder({ children }: {
    children: React.ReactNode;
}) {
    return <div className={contentBorderStyles}>{children}</div>;
}
const contentBorderStyles = css({
    border: `1px solid ${themeTokens.color.border.primary}`,
    borderRadius: borderRadius.medium,
});
